import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, PaginationQueryWithFields, Version } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { DefaultTask, UpsertDefaultTask } from "./types";

export const useDefaultTasksApi = () => {
  const api = useApi();

  const getAdimnDefaultTask = useCallback(
    (taskId: string, signal: AbortSignal) => {
      const url = `/admin/default_tasks/${taskId}`;
      return useSWRMutation<DefaultTask | null, CustomError, string>(url, (url) =>
        api.get<DefaultTask>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminDefaultTasks = useCallback(
    <K extends keyof DefaultTask>(query: PaginationQueryWithFields<Pick<DefaultTask, K>>, signal: AbortSignal) => {
      const url = buildUrl<Pick<DefaultTask, K>>("/admin/default_tasks", query);
      return useSWR<Paginated<Pick<DefaultTask, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Pick<DefaultTask, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminDefaultTasks = useCallback(
    async <K extends keyof DefaultTask>(
      query: PaginationQueryWithFields<Pick<DefaultTask, K>>,
      signal: AbortSignal,
    ) => {
      const url = buildUrl<Pick<DefaultTask, K>>("/admin/default_tasks", query);
      const x = await api.get<Paginated<Pick<DefaultTask, K>>>({ url, signal });
      return x.items;
    },
    [api],
  );

  const insertAdminDefaultTask = useCallback(
    async (body: UpsertDefaultTask) => {
      const url = `/admin/default_tasks`;
      await api.postNoResult<UpsertDefaultTask>(url, body).then(() => {
        api.mutateCache([`/admin/default_tasks`]);
      });
    },
    [api],
  );

  const updateAdminDefaultTask = useCallback(
    async (taskId: string, body: UpsertDefaultTask): Promise<void> => {
      const url = `/admin/default_tasks/${taskId}`;
      await api.put<UpsertDefaultTask>(url, body).then(() => {
        api.mutateCache(["/admin/default_tasks", `/admin/default_tasks/${taskId}/versions`]);
      });
    },
    [api],
  );

  const deleteAdminDefaultTask = useCallback(
    async (taskId: string): Promise<void> => {
      const url = `/admin/default_tasks/${taskId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/default_tasks`]);
      });
    },
    [api],
  );

  const getAdminDefaultTaskHistory = useCallback(
    (query: PaginationQuery, taskID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/default_tasks/${taskID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  return {
    getAdimnDefaultTask,
    getAdminDefaultTasks,
    useAdminDefaultTasks,
    insertAdminDefaultTask,
    updateAdminDefaultTask,
    deleteAdminDefaultTask,
    getAdminDefaultTaskHistory,
  };
};
